/**
 * DeploymentsSettings
 * Production deployment pipeline and history.
 * Rendered inside SettingsPage under /se/deployments.
 */

import { useState } from 'react';
import {
  Rocket, Check, X, Clock, GitCommit, RefreshCw, Shield,
  ChevronDown, ChevronUp, AlertTriangle, Loader2, Play,
} from 'lucide-react';
import {
  useDeployments,
  useDeploymentStatus,
  useApproveDeploy,
  useRollbackDeploy,
  useTriggerDeploy,
  useAgentTrust,
  type DeploymentRun,
  type DeploymentStatus,
} from '../../api/deployment';

const STATUS_CONFIG: Record<DeploymentStatus, { color: string; icon: typeof Check; label: string }> = {
  pending:     { color: 'text-blue-600 bg-blue-50',      icon: Clock,          label: 'Pending' },
  approved:    { color: 'text-cyan-600 bg-cyan-50',      icon: Check,          label: 'Approved' },
  building:    { color: 'text-amber-600 bg-amber-50',    icon: Loader2,        label: 'Building' },
  deploying:   { color: 'text-orange-600 bg-orange-50',  icon: Rocket,         label: 'Deploying' },
  verifying:   { color: 'text-purple-600 bg-purple-50',  icon: Shield,         label: 'Verifying' },
  success:     { color: 'text-green-600 bg-green-50',    icon: Check,          label: 'Success' },
  partial:     { color: 'text-amber-600 bg-amber-50',    icon: AlertTriangle,  label: 'Partial' },
  failed:      { color: 'text-red-600 bg-red-50',        icon: X,              label: 'Failed' },
  rolled_back: { color: 'text-red-600 bg-red-50',        icon: RefreshCw,      label: 'Rolled Back' },
  cancelled:   { color: 'text-muted bg-surface-subtle', icon: X,            label: 'Cancelled' },
};

function StatusBadge({ status }: { status: DeploymentStatus }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  const isAnimated = ['building', 'deploying', 'verifying'].includes(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className={`w-3 h-3 ${isAnimated ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

function TrustBadge() {
  const { data } = useAgentTrust();
  const trust = data?.data;
  if (!trust) return null;
  const tierColor = trust.autonomyTier === 'L3' ? 'text-green-600' : trust.autonomyTier === 'L2' ? 'text-cyan-600' : 'text-muted';
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-muted border border-line">
      <Shield className={`w-4 h-4 ${tierColor}`} />
      <span className="text-xs text-muted">Trust</span>
      <span className={`text-sm font-semibold ${tierColor}`}>{trust.trustScore}</span>
      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${tierColor} bg-white border border-line`}>{trust.autonomyTier}</span>
    </div>
  );
}

function DeploymentRow({ deploy }: { deploy: DeploymentRun }) {
  const [expanded, setExpanded] = useState(false);
  const { data: detail } = useDeploymentStatus(expanded ? deploy.id : null);
  const approveMut = useApproveDeploy();
  const rollbackMut = useRollbackDeploy();

  const canApprove = deploy.status === 'pending';
  const canRollback = ['success', 'partial'].includes(deploy.status);

  return (
    <div className="border-b border-line last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-surface-muted transition-colors"
      >
        <StatusBadge status={deploy.status} />
        <span className="flex items-center gap-1.5 text-sm font-mono text-text">
          <GitCommit className="w-3.5 h-3.5 text-subtle" />
          {deploy.targetCommit.slice(0, 7)}
        </span>
        <span className="text-xs text-muted">{deploy.environment}</span>
        <span className="text-xs text-muted">
          {deploy.servicesChanged.length} service{deploy.servicesChanged.length !== 1 ? 's' : ''}
        </span>
        <span className="text-xs text-muted">{deploy.triggeredBy}</span>
        <span className="text-xs text-muted ml-auto">
          {deploy.durationMs ? `${(deploy.durationMs / 1000).toFixed(0)}s` : '\u2014'}
        </span>
        <span className="text-xs text-subtle">
          {new Date(deploy.createdAt).toLocaleString()}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-subtle" /> : <ChevronDown className="w-4 h-4 text-subtle" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Actions */}
          <div className="flex items-center gap-2">
            {canApprove && (
              <button
                onClick={() => approveMut.mutate(deploy.id)}
                disabled={approveMut.isPending}
                className="px-3 py-1.5 text-xs font-medium rounded bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 disabled:opacity-50"
              >
                {approveMut.isPending ? 'Approving...' : 'Approve'}
              </button>
            )}
            {canRollback && (
              <button
                onClick={() => rollbackMut.mutate(deploy.id)}
                disabled={rollbackMut.isPending}
                className="px-3 py-1.5 text-xs font-medium rounded bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 disabled:opacity-50"
              >
                {rollbackMut.isPending ? 'Rolling back...' : 'Rollback'}
              </button>
            )}
            {deploy.errorMessage && (
              <span className="text-xs text-red-600 ml-2">{deploy.errorMessage}</span>
            )}
          </div>

          {/* Service results */}
          {detail?.data?.serviceResults && detail.data.serviceResults.length > 0 && (
            <div className="rounded-lg border border-line overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-surface-subtle text-muted">
                    <th className="text-left px-3 py-2">Service</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Build</th>
                    <th className="text-left px-3 py-2">Deploy</th>
                    <th className="text-left px-3 py-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.serviceResults.map(sr => (
                    <tr key={sr.id} className="border-t border-line">
                      <td className="px-3 py-1.5 text-text font-mono">{sr.serviceName}</td>
                      <td className="px-3 py-1.5">
                        <span className={`${sr.healthCheckPassed ? 'text-green-600' : 'text-red-600'}`}>
                          {sr.status}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-muted">
                        {sr.buildDurationMs ? `${(sr.buildDurationMs / 1000).toFixed(1)}s` : '\u2014'}
                      </td>
                      <td className="px-3 py-1.5 text-muted">
                        {sr.deployDurationMs ? `${(sr.deployDurationMs / 1000).toFixed(1)}s` : '\u2014'}
                      </td>
                      <td className="px-3 py-1.5 text-red-600 truncate max-w-[200px]">{sr.errorLog || '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Smoke test summary */}
          {deploy.smokeTestPassed !== null && (
            <div className="text-xs text-muted">
              Smoke tests: {deploy.smokeTestPassed
                ? <span className="text-green-600">Passed</span>
                : <span className="text-red-600">Failed</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DeploymentsSettings() {
  const { data, isLoading } = useDeployments();
  const triggerMut = useTriggerDeploy();
  const [showTrigger, setShowTrigger] = useState(false);

  const deployments = data?.data || [];

  return (
    <div className="space-y-6">
      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <TrustBadge />
        <button
          onClick={() => setShowTrigger(!showTrigger)}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
        >
          <Play className="w-4 h-4" />
          Trigger Deploy
        </button>
      </div>

      {/* Trigger form */}
      {showTrigger && (
        <div className="p-4 rounded-lg border border-line bg-surface-muted space-y-3">
          <p className="text-sm text-muted">Trigger a manual deployment from the latest main branch.</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                triggerMut.mutate({ environment: 'production', triggeredBy: 'manual' });
                setShowTrigger(false);
              }}
              disabled={triggerMut.isPending}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {triggerMut.isPending ? 'Triggering...' : 'Deploy to Production'}
            </button>
            <button
              onClick={() => {
                triggerMut.mutate({ environment: 'production', triggeredBy: 'manual', dryRun: true });
                setShowTrigger(false);
              }}
              disabled={triggerMut.isPending}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-surface-subtle text-text hover:bg-surface-subtle border border-line"
            >
              Dry Run
            </button>
            <button onClick={() => setShowTrigger(false)} className="px-3 py-2 text-sm text-muted hover:text-text">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Deployments list */}
      <div className="rounded-lg border border-line overflow-hidden bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-subtle animate-spin" />
          </div>
        ) : deployments.length === 0 ? (
          <div className="text-center py-12 text-muted text-sm">
            No deployments yet. Trigger one to get started.
          </div>
        ) : (
          deployments.map(d => <DeploymentRow key={d.id} deploy={d} />)
        )}
      </div>
    </div>
  );
}
